import { useState } from 'react'
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import RefreshIcon from '@mui/icons-material/Refresh'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Role, Site } from '@cmms/types'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { SearchableSelect } from '../../components/SearchableSelect'

/// Direct user-create dialog. Mirrors the InviteMemberDialog's
/// customer + site picker exactly, but adds password + phone fields
/// and skips the email-link step — the admin sets the credentials and
/// shares them out-of-band. Useful for field techs without email
/// accounts and for bulk onboarding from another system.
export function CreateUserDialog({
  open, onClose, roles,
}: {
  open: boolean
  onClose: () => void
  roles: Role[]
}) {
  const qc = useQueryClient()
  const [email, setEmail]       = useState('')
  const [fullName, setFullName] = useState('')
  const [phone, setPhone]       = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [roleId, setRoleId]     = useState('')
  const [siteIds, setSiteIds]   = useState<string[]>([])
  const [allSites, setAllSites] = useState(false)
  const [customerIds, setCustomerIds] = useState<string[]>([])
  const [isClient, setIsClient] = useState(false)
  const [isVendor, setIsVendor] = useState(false)
  // Free-text filter over the customer + site picker. Matches
  // customer names AND descendant site names, so an admin who knows
  // the site they need to grant access to can just type it in and
  // the owning customer will surface.
  const [scopeQuery, setScopeQuery] = useState('')

  const customersQ = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list(),
    staleTime: 5 * 60 * 1000,
  })
  const customers = Array.isArray(customersQ.data) ? customersQ.data : []

  const sitesQ = useQuery({
    queryKey: ['sites', 'all-customers'],
    queryFn: () => api.sites.list({ allCustomers: true }),
    staleTime: 5 * 60 * 1000,
    enabled: open,
  })
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const selectedRole = roles.find((r) => r.id === roleId)
  const isAdmin = selectedRole?.name === 'org_admin'
  const isCustomerAdmin = selectedRole?.name === 'customer_admin'
  const isMultiCustomer = customers.length > 1

  const sitesByCustomer = sites.reduce<Record<string, Site[]>>((acc, s) => {
    const key = s.customer_id ?? '__unassigned'
    ;(acc[key] ??= []).push(s)
    return acc
  }, {})

  function toggleCustomer(id: string) {
    setCustomerIds((prev) => {
      if (prev.includes(id)) {
        const dropped = (sitesByCustomer[id] ?? []).map((s) => s.id)
        setSiteIds((cur) => cur.filter((s) => !dropped.includes(s)))
        return prev.filter((c) => c !== id)
      }
      return [...prev, id]
    })
  }
  function toggleSite(id: string) {
    setSiteIds((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id])
  }
  function toggleAll() {
    if (allSites) { setAllSites(false); setSiteIds([]) }
    else { setAllSites(true); setSiteIds(sites.map((s) => s.id)) }
  }

  /// Generate a memorable-but-strong default password the admin can
  /// hand over verbally — three short syllables + 3 digits clear the
  /// 8-char minimum and stay easy to type.
  function generatePassword() {
    const syl = ['ka', 'mo', 'ze', 'ru', 'pa', 'lu', 'ti', 'sa', 'no', 'vi']
    const pick = () => syl[Math.floor(Math.random() * syl.length)]
    const n = Math.floor(Math.random() * 900 + 100)
    setPassword(`${pick()}${pick()}${pick()}${n}`)
    setShowPassword(true)
  }

  function reset() {
    setEmail(''); setFullName(''); setPhone(''); setPassword('')
    setShowPassword(false); setRoleId(''); setSiteIds([])
    setAllSites(false); setCustomerIds([]); setIsClient(false); setIsVendor(false)
  }

  const createM = useMutation({
    mutationFn: () =>
      api.users.create({
        name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        password,
        role: selectedRole?.name ?? '',
        customer_ids: isAdmin
          ? undefined
          : isMultiCustomer && customerIds.length > 0
            ? customerIds
            : undefined,
        site_ids: isAdmin || isCustomerAdmin
          ? undefined
          : siteIds.length > 0 ? siteIds : undefined,
        is_client: isClient,
        is_vendor: isVendor,
      }),
    onSuccess: (user) => {
      toast.success(`Created ${user.name}`)
      qc.invalidateQueries({ queryKey: ['users'] })
      reset()
      onClose()
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : 'Failed to create user'),
  })

  const passwordOk = password.length >= 8

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Create user</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2.5 }}>
          The user account is created immediately with the credentials you set here — share them
          with the user out-of-band. Use the Invite flow instead if you want them to set their own password.
        </Typography>
        <Stack spacing={2}>
          <TextField label="Full name" required value={fullName} onChange={(e) => setFullName(e.target.value)}
            size="small" fullWidth autoFocus placeholder="e.g. John Smith"
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

          <TextField label="Email address" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            size="small" fullWidth placeholder="john@example.com"
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

          <TextField label="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)}
            size="small" fullWidth placeholder="+91 98765 43210"
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

          <TextField
            label="Password" required value={password} onChange={(e) => setPassword(e.target.value)}
            type={showPassword ? 'text' : 'password'} size="small" fullWidth
            placeholder="At least 8 characters"
            helperText={password.length > 0 && !passwordOk ? 'Use 8+ characters' : 'Share this with the user — they can change it later.'}
            error={password.length > 0 && !passwordOk}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="Generate a memorable password" arrow>
                    <IconButton size="small" onClick={generatePassword}
                      sx={{ color: 'primary.main' }}>
                      <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setShowPassword((v) => !v)}>
                    {showPassword
                      ? <VisibilityOffIcon sx={{ fontSize: 16 }} />
                      : <VisibilityIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }} />

          <Box>
            <SearchableSelect
              value={roleId}
              onChange={setRoleId}
              options={roles}
              getValue={(r) => r.id}
              getLabel={(r) => `${r.name}${r.is_system ? ' (system)' : ''}`}
              placeholder="Select a role *"
            />
          </Box>

          {/* Client / internal tag — display only, no permission side
              effects. Splits the People page into Internal / Client
              sub-tabs but leaves role, customer, and site access
              unchanged. */}
          <FormControlLabel
            control={<Checkbox size="small" checked={isClient}
              onChange={(e) => setIsClient(e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: 13 }}>
                This user is a client
                <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary', ml: 0.75 }}>
                  Shown under the Clients tab. Doesn't change what they can access.
                </Typography>
              </Typography>
            }
            sx={{ mx: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.25 } }}
          />

          {/* Vendor tag — same display-only semantics as the client tag
              above. No relation to the separate vendors catalog. */}
          <FormControlLabel
            control={<Checkbox size="small" checked={isVendor}
              onChange={(e) => setIsVendor(e.target.checked)} />}
            label={
              <Typography sx={{ fontSize: 13 }}>
                This user is a vendor
                <Typography component="span" sx={{ fontSize: 11.5, color: 'text.secondary', ml: 0.75 }}>
                  Shown under the Vendors tab. Doesn't change what they can access.
                </Typography>
              </Typography>
            }
            sx={{ mx: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { pt: 0.25 } }}
          />

          {/* ── Multi-customer scope picker (mirror of invite) ── */}
          {roleId && !isAdmin && isMultiCustomer && (() => {
            // Filter derivation lives inside the render branch so it
            // only runs when the picker is actually shown. Query
            // matches customer names OR any of their site names, so an
            // admin who only remembers the site can still find the
            // owning customer.
            const q = scopeQuery.trim().toLowerCase()
            const filteredCustomers = q === ''
              ? customers
              : customers.filter((c) => {
                  if (c.name.toLowerCase().includes(q)) return true
                  return (sitesByCustomer[c.id] ?? [])
                    .some((s) => s.name.toLowerCase().includes(q))
                })
            return (
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <BusinessOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                  {isCustomerAdmin ? 'Customers they can manage' : 'Customer + site access'}
                </Typography>
              </Stack>
              {/* Search — narrows the list to customers whose name or
                  descendant sites match. Useful when the org has
                  dozens of customers and hundreds of sites (e.g. the
                  Bacsys / AWFIS / DBS accounts). */}
              <TextField
                size="small" fullWidth
                value={scopeQuery}
                onChange={(e) => setScopeQuery(e.target.value)}
                placeholder="Search customers or sites"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: 13 } }}
              />
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, maxHeight: 240, overflowY: 'auto' }}>
                {filteredCustomers.length === 0 && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    No customers or sites match your search.
                  </Typography>
                )}
                {filteredCustomers.map((c, ci) => {
                  const checked = customerIds.includes(c.id)
                  const customerSites = sitesByCustomer[c.id] ?? []
                  // When search is active and only the SITE name
                  // matched (not the customer name), narrow the
                  // rendered sites to just those hits so the admin's
                  // eye lands on the match instead of scanning the
                  // whole site list under a picked customer.
                  const siteQueryMatchesCustomer = q === '' || c.name.toLowerCase().includes(q)
                  const visibleCustomerSites = siteQueryMatchesCustomer
                    ? customerSites
                    : customerSites.filter((s) => s.name.toLowerCase().includes(q))
                  return (
                    <Box key={c.id} sx={{
                      borderTop: ci === 0 ? 'none' : '1px solid',
                      borderColor: 'divider',
                    }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', px: 1.5, py: 0.75,
                        bgcolor: checked
                          ? (t) => `${t.palette.primary.main}08`
                          : (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
                      }}>
                        <FormControlLabel
                          control={<Checkbox size="small" checked={checked} onChange={() => toggleCustomer(c.id)} sx={{ py: 0.25 }} />}
                          label={
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{c.name}</Typography>
                              {typeof c.site_count === 'number' && (
                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                  {c.site_count} site{c.site_count === 1 ? '' : 's'}
                                </Typography>
                              )}
                            </Stack>
                          }
                          sx={{ m: 0, flex: 1 }}
                        />
                      </Box>
                      {checked && !isCustomerAdmin && visibleCustomerSites.length > 0 && (() => {
                        // Per-customer select-all — bulk-picks every
                        // currently-visible site (search-narrowed if
                        // active). Toggles based on whether every
                        // visible site under THIS customer is already
                        // in `siteIds`.
                        const visIds = visibleCustomerSites.map((s) => s.id)
                        const pickedFromVisible = visIds.filter((id) => siteIds.includes(id)).length
                        const allVisiblePicked = pickedFromVisible === visIds.length
                        const label = q === ''
                          ? `Select all ${visIds.length}`
                          : `Select all ${visIds.length} matching`
                        return (
                          <Stack sx={{ pl: 4, py: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
                              <Button size="small"
                                onClick={() => setSiteIds((prev) => {
                                  if (allVisiblePicked) {
                                    const drop = new Set(visIds)
                                    return prev.filter((id) => !drop.has(id))
                                  }
                                  return Array.from(new Set([...prev, ...visIds]))
                                })}
                                sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', color: 'primary.main', minWidth: 0, py: 0.25 }}>
                                {allVisiblePicked ? 'Deselect all' : label}
                              </Button>
                              <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                                {pickedFromVisible} / {visIds.length}
                              </Typography>
                            </Stack>
                            {visibleCustomerSites.map((s) => (
                              <FormControlLabel key={s.id}
                                control={<Checkbox size="small" checked={siteIds.includes(s.id)} onChange={() => toggleSite(s.id)} sx={{ py: 0.25 }} />}
                                label={<Typography sx={{ fontSize: 12.5 }}>{s.name}</Typography>}
                                sx={{ pl: 0.5, m: 0 }}
                              />
                            ))}
                          </Stack>
                        )
                      })()}
                    </Box>
                  )
                })}
              </Box>
              {customerIds.length === 0 && (
                <Typography sx={{ fontSize: 11, color: '#D97706', mt: 0.75 }}>
                  Pick at least one customer — this person can't see anything otherwise.
                </Typography>
              )}
            </Box>
            )
          })()}

          {/* ── Single-customer org: flat site picker ── */}
          {roleId && !isAdmin && !isMultiCustomer && sites.length > 0 && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
                <LocationOnOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>
                  Site access
                </Typography>
              </Stack>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
                <Box sx={{ px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider',
                  bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={allSites} onChange={toggleAll} sx={{ py: 0.25 }} />}
                    label={<Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>All sites</Typography>}
                  />
                </Box>
                <Stack sx={{ maxHeight: 160, overflowY: 'auto', scrollbarWidth: 'thin' }}>
                  {sites.map((s) => (
                    <FormControlLabel key={s.id}
                      control={<Checkbox size="small" checked={siteIds.includes(s.id)} onChange={() => toggleSite(s.id)} sx={{ py: 0.25 }} />}
                      label={<Typography sx={{ fontSize: 12.5 }}>{s.name}</Typography>}
                      sx={{ px: 1.5, py: 0.5, m: 0, borderBottom: '1px solid', borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' } }}
                    />
                  ))}
                </Stack>
              </Box>
            </Box>
          )}
          {isAdmin && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1, py: 0.75, borderRadius: 1, bgcolor: (t) => `${t.palette.primary.main}08` }}>
              <LocationOnOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              <Typography sx={{ fontSize: 12, color: 'primary.main', fontWeight: 500 }}>
                Admins automatically have access to all sites and customers.
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={() => createM.mutate()}
          disabled={
            !email.trim() || !fullName.trim() || !roleId || !passwordOk || createM.isPending
            || (!isAdmin && isMultiCustomer && customerIds.length === 0)
          }
          sx={{ fontSize: 13, fontWeight: 600 }}>
          {createM.isPending ? 'Creating…' : 'Create user'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
